import React, { useCallback, useEffect, useState, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Button,
  PageHeader,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Badge,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
  Checkbox,
  Input,
  SpinnerLoader,
  DataTable,
  SingleSelect,
} from "lift-ui";
import type { TableColumn } from "lift-ui";
import {
  SortingState,
  RowSelectionState,
} from "@tanstack/react-table";
import {
  Search,
  GitMerge,
  Plus,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
} from "lucide-react";

import TaxonomyMergeTypeExistingDialog from "./components/TaxonomyMergeExistingDialog";
import TaxonomyMergeCreateDialog from "./components/TaxonomyMergeCreateDialog";
import TaxonomyMergeValueExistingDialog from "./components/TaxonomyMergeValueExistingDialog";
import TaxonomyMergeValueCreateDialog from "./components/TaxonomyMergeValueCreateDialog";

import { Toastify } from 'lift-ui';
import apiClient from "@lift-admin/utils/apiClient";
import { useAuthContext } from "@lift-admin/utils/provider";

const TAXONOMY_TYPES = ["campaign", "ad", "adset"];

// Type Definitions
export interface CDValue {
  id: number;
  value: string;
  display_name: string;
  custom_domain_type_id: number;
  created_by: number;
  brand_id: number;
  created_at: Date;
  brand_name: string;
  updated_at: Date;
  is_active: boolean;
  user_info: {
    name: string;
    image?: string;
    [key: string]: any;
  };
}

export interface MergedCDValue extends CDValue {
  merged_user_info: {
    name: string;
    image?: string;
    [key: string]: any;
  };
  brand_name: string;
  merged_vals: CDValue[];
  merged_value_name_id: number;
  parent: CDValue;
  children: CDValue[];
}

export interface CDType {
  id: number;
  name: string;
  taxonomy_type: string;
  description?: string;
  brand_name: string;
  is_active: boolean;
  is_mandatory: boolean;
  created_by: number;
  brand_id: number;
  user_info: {
    name: string;
    image?: string;
    [key: string]: any;
  };
}

export interface MergedCDType extends CDType {
  merged_user_info: {
    name: string;
    image?: string;
    [key: string]: any;
  };
  brand_name: string;
  is_active: boolean;
  merged_cols: CDType[];
  merged_column_name_id: number;
}

export interface MergedCDValueGroup {
  parent: CDValue;
  children: CDValue[];
}

// Error Handler
export const handleApiError = (error: any) => {
  console.error("API Error:", error);
  const errorMessage =
    error.response?.data?.detail ||
    error.message ||
    "An unexpected error occurred";
  Toastify("error", errorMessage);
  return errorMessage;
};

// Main Component
const TaxonomyMerge: React.FC = () => {
  // Permission checks for taxonomy management
  const { canEdit, canManage } = useAuthContext();
  const hasEditAccess = canEdit('taxonomy');
  const hasManageAccess = canManage('taxonomy');
  
  // State Management
  const [mainTab, setMainTab] = useState<"types" | "values">("types");
  const [subTab, setSubTab] = useState<"merged" | "non-merged">("merged");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [callTaxonomyMerge, setCallTaxonomyMerge] = useState(false);
  const [globalTaxonomyType, setGlobalTaxonomyType] = useState("campaign");
  const [sorting, setSorting] = useState<SortingState>([]);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [searchQuery, setSearchQuery] = useState("");
  const [typesPageIndex, setTypesPageIndex] = useState(0);
  const [typesPageSize, setTypesPageSize] = useState(50);
  const [valuesPageIndex, setValuesPageIndex] = useState(0);
  const [valuesPageSize, setValuesPageSize] = useState(50);
  const [mergedTypesPageIndex, setMergedTypesPageIndex] = useState(0);
  const [mergedTypesPageSize, setMergedTypesPageSize] = useState(50);

  // Data States
  const [nonMergedTypes, setNonMergedTypes] = useState<CDType[]>([]);
  const [mergedTypes, setMergedTypes] = useState<MergedCDType[]>([]);
  const [nonMergedValues, setNonMergedValues] = useState<CDValue[]>([]);
  const [mergedValues, setMergedValues] = useState<MergedCDValue[]>([]);
  
  // Loading states for counts
  const [isLoadingCounts, setIsLoadingCounts] = useState(true);

  // Dialog States
  const [openTypeMergeDialog, setOpenTypeMergeDialog] = useState(false);
  const [openTypeCreateDialog, setOpenTypeCreateDialog] = useState(false);
  const [openValueMergeDialog, setOpenValueMergeDialog] = useState(false);
  const [openValueCreateDialog, setOpenValueCreateDialog] = useState(false);

  // Accordion expand state for merged types
  const [expandedMergedTypes, setExpandedMergedTypes] = useState<Record<number, boolean>>({});
  const [childColumnsByTypeId, setChildColumnsByTypeId] = useState<Record<number, MergedCDType[]>>({});
  const [childLoadingByTypeId, setChildLoadingByTypeId] = useState<Record<number, boolean>>({});
  // Accordion expand state for merged values
  const [expandedMergedValues, setExpandedMergedValues] = useState<Record<number, boolean>>({});
  const [mergedValuesPageIndex, setMergedValuesPageIndex] = useState(0);
  const [mergedValuesPageSize, setMergedValuesPageSize] = useState(50);

  const toggleMergedTypeRow = useCallback((typeId: number) => {
    setExpandedMergedTypes((prev) => {
      const isOpen = !prev[typeId];
      if (isOpen && !childColumnsByTypeId[typeId]) {
        setChildLoadingByTypeId((p) => ({ ...p, [typeId]: true }));
        apiClient
          .get<{ child_cdtypes: MergedCDType[] }>(
            `/txnmy/api/v1/cdtype/cdtypes/children/${typeId}`,
            { headers: { "Content-Type": "application/json" } }
          )
          .then((res) => {
            setChildColumnsByTypeId((p) => ({ ...p, [typeId]: res.data.child_cdtypes ?? [] }));
          })
          .catch(() => {
            setChildColumnsByTypeId((p) => ({ ...p, [typeId]: [] }));
          })
          .finally(() => {
            setChildLoadingByTypeId((p) => ({ ...p, [typeId]: false }));
          });
      }
      return { ...prev, [typeId]: isOpen };
    });
  }, [childColumnsByTypeId]);

  const toggleMergedValueRow = useCallback((valueId: number) => {
    setExpandedMergedValues((prev) => ({ ...prev, [valueId]: !prev[valueId] }));
  }, []);

  // Tab Management
  const handleMainTabChange = useCallback((value: string) => {
    setMainTab(value as "types" | "values");
    setSubTab("merged");
    setRowSelection({});
  }, []);

  const handleSubTabChange = useCallback((value: string) => {
    setSubTab(value as "merged" | "non-merged");
    setRowSelection({});
  }, []);

  // Data Fetching - fetch all data (both merged and non-merged) for accurate counts
  const fetchAllData = useCallback(async () => {
    setIsLoading(true);
    setIsLoadingCounts(true);
    setError(null);
    try {
      const [
        mergedTypesResponse,
        unmergedTypesResponse,
        mergedValuesResponse,
        unmergedValuesResponse,
      ] = await Promise.all([
        apiClient.get(
          `/txnmy/api/v1/cdtype/merged${globalTaxonomyType ? `?tax_type=${globalTaxonomyType}` : ""}`
        ),
        apiClient.get(
          `/txnmy/api/v1/cdtype/unmerged${globalTaxonomyType ? `?tax_type=${globalTaxonomyType}` : ""}`
        ),
        apiClient.get(`/txnmy/api/v1/cdvalue/merged`),
        apiClient.get(`/txnmy/api/v1/cdvalue/unmerged`),
      ]);

      if (mergedTypesResponse.data.message === "success") {
        setMergedTypes(mergedTypesResponse.data.response);
      }

      if (unmergedTypesResponse.data.message === "success") {
        setNonMergedTypes(unmergedTypesResponse.data.response);
      }

      if (mergedValuesResponse.data.message === "success") {
        setMergedValues(mergedValuesResponse.data.response);
      }

      if (unmergedValuesResponse.data.message === "success") {
        setNonMergedValues(unmergedValuesResponse.data.response);
      }
    } catch (error: any) {
      setError(handleApiError(error));
    } finally {
      setIsLoading(false);
      setIsLoadingCounts(false);
    }
  }, [globalTaxonomyType]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData, callTaxonomyMerge]);

  // Filter data based on search
  const filteredNonMergedTypes = useMemo(() => {
  const searchLower = searchQuery.toLowerCase();
    return nonMergedTypes.filter(
      (t) =>
        (!globalTaxonomyType || t.taxonomy_type === globalTaxonomyType) &&
    t.name.toLowerCase().includes(searchLower)
  );
  }, [nonMergedTypes, globalTaxonomyType, searchQuery]);

  const filteredMergedTypes = useMemo(() => {
    const searchLower = searchQuery.toLowerCase();
    return mergedTypes.filter((t) => t.name.toLowerCase().includes(searchLower));
  }, [mergedTypes, searchQuery]);

  const filteredNonMergedValues = useMemo(() => {
    const searchLower = searchQuery.toLowerCase();
    return nonMergedValues.filter((v) => v.value.toLowerCase().includes(searchLower));
  }, [nonMergedValues, searchQuery]);

  const filteredMergedValues = useMemo(() => {
    const searchLower = searchQuery.toLowerCase();
    return mergedValues.filter((g) => g.parent.value.toLowerCase().includes(searchLower));
  }, [mergedValues, searchQuery]);

  const paginatedMergedTypes = useMemo(
    () =>
      filteredMergedTypes.slice(
        mergedTypesPageIndex * mergedTypesPageSize,
        mergedTypesPageIndex * mergedTypesPageSize + mergedTypesPageSize
      ),
    [filteredMergedTypes, mergedTypesPageIndex, mergedTypesPageSize]
  );

  const paginatedMergedValues = useMemo(
    () =>
      filteredMergedValues.slice(
        mergedValuesPageIndex * mergedValuesPageSize,
        mergedValuesPageIndex * mergedValuesPageSize + mergedValuesPageSize
      ),
    [filteredMergedValues, mergedValuesPageIndex, mergedValuesPageSize]
  );

  // Sort and paginate non-merged types for DataTable
  const sortKey = sorting[0]?.id;
  const sortDesc = sorting[0]?.desc ?? false;
  const sortedFilteredNonMergedTypes = useMemo(() => {
    if (!sortKey) return filteredNonMergedTypes;
    return [...filteredNonMergedTypes].sort((a, b) => {
      const aVal = (a as unknown as Record<string, unknown>)[sortKey];
      const bVal = (b as unknown as Record<string, unknown>)[sortKey];
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      const cmp = String(aVal).localeCompare(String(bVal), undefined, { numeric: true });
      return sortDesc ? -cmp : cmp;
    });
  }, [filteredNonMergedTypes, sortKey, sortDesc]);
  const sortedFilteredNonMergedValues = useMemo(() => {
    if (!sortKey) return filteredNonMergedValues;
    return [...filteredNonMergedValues].sort((a, b) => {
      const aVal = (a as unknown as Record<string, unknown>)[sortKey];
      const bVal = (b as unknown as Record<string, unknown>)[sortKey];
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      const cmp = String(aVal).localeCompare(String(bVal), undefined, { numeric: true });
      return sortDesc ? -cmp : cmp;
    });
  }, [filteredNonMergedValues, sortKey, sortDesc]);
  const paginatedNonMergedTypes = useMemo(
    () => sortedFilteredNonMergedTypes.slice(typesPageIndex * typesPageSize, typesPageIndex * typesPageSize + typesPageSize),
    [sortedFilteredNonMergedTypes, typesPageIndex, typesPageSize]
  );
  const paginatedNonMergedValues = useMemo(
    () => sortedFilteredNonMergedValues.slice(valuesPageIndex * valuesPageSize, valuesPageIndex * valuesPageSize + valuesPageSize),
    [sortedFilteredNonMergedValues, valuesPageIndex, valuesPageSize]
  );

  // DataTable columns for Non-Merged Types
  const nonMergedTypesDataTableColumns = useMemo((): TableColumn<CDType>[] => {
    const cols: TableColumn<CDType>[] = [];
    if (hasEditAccess) {
      cols.push({
        key: "select",
        header: (
          <Checkbox
            checked={paginatedNonMergedTypes.length > 0 && paginatedNonMergedTypes.every((r) => rowSelection[r.id])}
            onCheckedChange={(checked) => {
              setRowSelection((prev) => {
                const next = { ...prev };
                paginatedNonMergedTypes.forEach((r) => {
                  next[r.id] = !!checked;
                });
                return next;
              });
            }}
          />
        ),
        accessor: (row) => row.id,
        width: "32px",
        align: "center",
        render: (_v, row) => (
          <Checkbox
            checked={!!rowSelection[row.id]}
            onCheckedChange={() => {
              setRowSelection((prev) => ({ ...prev, [row.id]: !prev[row.id] }));
            }}
          />
        ),
      });
    }
    cols.push(
      { key: "name", header: "Name", accessor: (row) => row.name, sortable: true, width: "15%", render: (_v, row) => <span className="font-medium">{row.name}</span> },
      { key: "taxonomy_type", header: "Type", accessor: (row) => row.taxonomy_type, width: "12%" },
      { key: "brand_name", header: "Brand Name", accessor: (row) => row.brand_name ?? "N/A", width: "12%" },
      { key: "brand_id", header: "Brand ID", accessor: (row) => String(row.brand_id ?? "N/A"), width: "10%" },
      {
        key: "is_active",
        header: "Active",
        accessor: (row) => row.is_active,
        width: "10%",
        render: (_v, row) => (
          <Badge variant={row.is_active ? "default" : "secondary"}>{row.is_active ? "Yes" : "No"}</Badge>
        ),
      },
      {
        key: "is_mandatory",
        header: "Status",
        accessor: (row) => row.is_mandatory,
        width: "10%",
        render: (_v, row) => (
          <Badge variant="outline">{row.is_mandatory ? "Required" : "Optional"}</Badge>
        ),
      },
      {
        key: "user_info",
        header: "Created By",
        accessor: (row) => row.user_info?.name ?? "N/A",
        width: "15%",
        render: (_v, row) => (
            <div className="flex items-center gap-2">
            {row.user_info?.image && (
              <img src={row.user_info.image} alt="" className="w-6 h-6 rounded-full object-cover" />
              )}
            <span>{row.user_info?.name || "N/A"}</span>
            </div>
        ),
      }
    );
    return cols;
  }, [hasEditAccess, rowSelection, paginatedNonMergedTypes]);

  // DataTable columns for Non-Merged Values
  const nonMergedValuesDataTableColumns = useMemo((): TableColumn<CDValue>[] => {
    const cols: TableColumn<CDValue>[] = [];
    if (hasEditAccess) {
      cols.push({
        key: "select",
        header: (
          <Checkbox
            checked={paginatedNonMergedValues.length > 0 && paginatedNonMergedValues.every((r) => rowSelection[r.id])}
            onCheckedChange={(checked) => {
              setRowSelection((prev) => {
                const next = { ...prev };
                paginatedNonMergedValues.forEach((r) => {
                  next[r.id] = !!checked;
                });
                return next;
              });
            }}
          />
        ),
        accessor: (row) => row.id,
        width: "32px",
        align: "center",
        render: (_v, row) => (
          <Checkbox
            checked={!!rowSelection[row.id]}
            onCheckedChange={() => {
              setRowSelection((prev) => ({ ...prev, [row.id]: !prev[row.id] }));
            }}
          />
        ),
      });
    }
    cols.push(
      { key: "value", header: "Name", accessor: (row) => row.value, sortable: true, width: "15%", render: (_v, row) => <span className="font-medium">{row.value}</span> },
      { key: "custom_domain_type_id", header: "Type", accessor: (row) => String(row.custom_domain_type_id), width: "12%" },
      { key: "brand_name", header: "Brand Name", accessor: (row) => row.brand_name ?? "N/A", width: "12%" },
      { key: "brand_id", header: "Brand ID", accessor: (row) => String(row.brand_id ?? "N/A"), width: "10%" },
      {
        key: "is_active",
        header: "Active",
        accessor: (row) => row.is_active,
        width: "10%",
        render: (_v, row) => (
          <Badge variant={row.is_active ? "default" : "secondary"}>{row.is_active ? "Yes" : "No"}</Badge>
        ),
      },
      {
        key: "user_info",
        header: "Created By",
        accessor: (row) => row.user_info?.name ?? "N/A",
        width: "15%",
        render: (_v, row) => (
          <div className="flex items-center gap-2">
            {row.user_info?.image && (
              <img src={row.user_info.image} alt="" className="w-6 h-6 rounded-full object-cover" />
            )}
            <span>{row.user_info?.name || "N/A"}</span>
          </div>
        ),
      }
    );
    return cols;
  }, [hasEditAccess, rowSelection, paginatedNonMergedValues]);

  // Inline child columns for accordion (merged types)
  const childTypeColumns = useMemo((): TableColumn<MergedCDType>[] => [
    { key: "name", header: "Column Name", accessor: (row) => row.name, width: "25%", render: (_v, row) => <span className="font-medium">{row.name}</span> },
    { key: "taxonomy_type", header: "Type", accessor: (row) => row.taxonomy_type, width: "18%" },
    { key: "is_mandatory", header: "Mandatory", accessor: (row) => row.is_mandatory, width: "15%", render: (_v, row) => <Badge variant={row.is_mandatory ? "default" : "secondary"}>{row.is_mandatory ? "Yes" : "No"}</Badge> },
    { key: "brand_name", header: "Brand", accessor: (row) => row.brand_name ?? "N/A", width: "22%" },
    { key: "user_info", header: "Created By", accessor: (row) => row.user_info?.name ?? "N/A", width: "20%", render: (_v, row) => row?.user_info?.name || "N/A" },
  ], []);

  // Inline child values for accordion (merged values)
  const childValueColumns = useMemo((): TableColumn<CDValue>[] => [
    { key: "value", header: "Value", accessor: (row) => row.display_name || row.value, width: "28%", render: (_v, row) => <span className="font-medium">{row.display_name || row.value}</span> },
    { key: "custom_domain_type_id", header: "Type", accessor: (row) => String(row.custom_domain_type_id), width: "22%" },
    { key: "brand_name", header: "Brand", accessor: (row) => row.brand_name ?? "N/A", width: "25%" },
    { key: "user_info", header: "Created By", accessor: (row) => row.user_info?.name ?? "N/A", width: "25%", render: (_v, row) => row?.user_info?.name || "N/A" },
  ], []);

  // Get selected IDs
  const selectedTypeIds = Object.keys(rowSelection)
    .filter((key) => rowSelection[key])
    .map((id) => parseInt(id, 10));
  const selectedValueIds = Object.keys(rowSelection)
    .filter((key) => rowSelection[key])
    .map((id) => parseInt(id, 10));

  const hasSelection = mainTab === "types" ? selectedTypeIds.length > 0 : selectedValueIds.length > 0;

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <PageHeader
        title="Merge Taxonomy Column"
        renderCustomTop={
          <div className="flex items-center gap-3 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchAllData()}
              disabled={isLoading}
              className="gap-1 px-3 h-8 text-sm"
            >
              <RefreshCw
                className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`}
              />
              Refresh
            </Button>
            {mainTab === "types" && (
              <SingleSelect
                options={TAXONOMY_TYPES.map((type) => ({
                  label: type.charAt(0).toUpperCase() + type.slice(1),
                  value: type,
                }))}
                defaultValue={globalTaxonomyType}
                onChange={(type) => {
                  setGlobalTaxonomyType(type);
                  setRowSelection({});
                }}
                placeholder="Taxonomy Type"
                containerClassName="w-[180px]"
              />
            )}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 w-64 h-8"
              />
            </div>
          </div>
        }
      />

      <div className="flex-1 overflow-hidden flex flex-col min-h-0 px-8 pt-4 pb-4">
        {/* Main Tabs */}
        <Tabs value={mainTab} onValueChange={handleMainTabChange} className="w-full flex-1 flex flex-col min-h-0">
          <TabsList className="inline-flex w-fit mb-4">
            <TabsTrigger value="types" className="px-6">Types</TabsTrigger>
            <TabsTrigger value="values" className="px-6">Values</TabsTrigger>
          </TabsList>

          {/* Sub Tabs & Controls */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
            <Tabs value={subTab} onValueChange={handleSubTabChange} className="w-fit">
              <TabsList className="inline-flex w-fit">
                <TabsTrigger value="merged" className="gap-2">
                  Merged {mainTab === "types" ? "Types" : "Values"}
                  <Badge variant="secondary" className="ml-1">
                    {isLoadingCounts ? (
                      <SpinnerLoader className="h-3 w-3" />
                    ) : (
                      mainTab === "types" ? mergedTypes.length : mergedValues.length
                    )}
                  </Badge>
                </TabsTrigger>
                <TabsTrigger value="non-merged" className="gap-2">
                  Non-Merged {mainTab === "types" ? "Types" : "Values"}
                  <Badge variant="secondary" className="ml-1">
                    {isLoadingCounts ? (
                      <SpinnerLoader className="h-3 w-3" />
                    ) : (
                      mainTab === "types" ? nonMergedTypes.length : nonMergedValues.length
                    )}
                  </Badge>
                </TabsTrigger>
              </TabsList>
          </Tabs>

            {/* Action Buttons */}
            {subTab === "non-merged" && hasEditAccess && (
              <div className="flex items-center gap-2">
                {hasSelection && (
                  <span className="text-sm text-muted-foreground">
                    {mainTab === "types"
                      ? `${selectedTypeIds.length} type(s) selected`
                      : `${selectedValueIds.length} value(s) selected`}
                  </span>
                )}
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="default"
                        size="sm"
                        onClick={() =>
                          mainTab === "types"
                            ? setOpenTypeMergeDialog(true)
                            : setOpenValueMergeDialog(true)
                        }
                        disabled={!hasSelection}
                        className="gap-2"
                      >
                        <GitMerge className="h-4 w-4" />
                        Merge
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      Merge with existing {mainTab === "types" ? "type" : "value"}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() =>
                          mainTab === "types"
                            ? setOpenTypeCreateDialog(true)
                            : setOpenValueCreateDialog(true)
                        }
                        disabled={!hasSelection}
                        className="gap-2"
                      >
                        <Plus className="h-4 w-4" />
                        Create New
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      Create new merged {mainTab === "types" ? "type" : "value"}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            )}
          </div>

          {/* Error Message */}
        {error && (
            <div className="bg-destructive/10 border border-destructive/20 text-destructive rounded-lg p-4 mb-4 flex items-center justify-between">
              <span>{error}</span>
              <Button variant="ghost" size="sm" onClick={() => setError(null)}>
                Dismiss
              </Button>
            </div>
        )}

        {/* Types Content */}
          <TabsContent value="types" className="mt-0 flex-1 flex flex-col min-h-0">
            <div className="flex-1 flex flex-col bg-white min-h-0 rounded-lg shadow-sm overflow-hidden">
              <div className="flex-1 overflow-auto min-h-0">
                {isLoading ? (
                  <div className="flex items-center justify-center py-20">
                    <SpinnerLoader />
                  </div>
                ) : subTab === "merged" ? (
                  <div className="w-full flex flex-col flex-1 min-h-0">
                    <div className="overflow-auto relative flex-1" style={{ maxHeight: "calc(100vh - 350px)", minHeight: "calc(100vh - 350px)" }}>
                      <Table>
                        <TableHeader className="sticky top-[-1px] bg-background z-10">
                          <TableRow>
                            <TableHead className="w-12"></TableHead>
                            <TableHead className="font-semibold">Name</TableHead>
                            <TableHead className="font-semibold">Type</TableHead>
                            <TableHead className="font-semibold">Brand Name</TableHead>
                            <TableHead className="font-semibold">Brand ID</TableHead>
                            <TableHead className="font-semibold">Active</TableHead>
                            <TableHead className="font-semibold">Status</TableHead>
                            <TableHead className="font-semibold">Created By</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {paginatedMergedTypes.length > 0 ? (
                            paginatedMergedTypes.map((row) => (
                              <React.Fragment key={row.id}>
                                <TableRow className="hover:bg-muted/50 cursor-pointer" onClick={() => toggleMergedTypeRow(row.id)}>
                                  <TableCell className="w-12">
                                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={(e) => { e.stopPropagation(); toggleMergedTypeRow(row.id); }}>
                                      {expandedMergedTypes[row.id] ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                    </Button>
                                  </TableCell>
                                  <TableCell>
                                    <div className="flex items-center gap-2">
                                      <span className="font-medium">{row.name}</span>
                                      {row.is_mandatory && <Badge variant="default" className="text-xs">Mandatory</Badge>}
                                    </div>
                                  </TableCell>
                                  <TableCell>{row.taxonomy_type}</TableCell>
                                  <TableCell>{row.brand_name ?? "N/A"}</TableCell>
                                  <TableCell>{row.brand_id ?? "N/A"}</TableCell>
                                  <TableCell><Badge variant={row.is_active ? "default" : "secondary"}>{row.is_active ? "Yes" : "No"}</Badge></TableCell>
                                  <TableCell><Badge variant="outline">{row.is_mandatory ? "Required" : "Optional"}</Badge></TableCell>
                                  <TableCell>
                                    <div className="flex items-center gap-2">
                                      {row.user_info?.image && <img src={row.user_info.image} alt="" className="w-6 h-6 rounded-full object-cover" />}
                                      <span>{row.user_info?.name || "N/A"}</span>
                                    </div>
                                  </TableCell>
                                </TableRow>
                                {expandedMergedTypes[row.id] && (
                                  <TableRow>
                                    <TableCell colSpan={8} className="p-0">
                                      <div className="m-4">
                                        <p className="text-sm font-medium mb-2">Child Columns for {row.name}</p>
                                        {childLoadingByTypeId[row.id] ? (
                                          <div className="flex items-center justify-center py-4"><SpinnerLoader /></div>
                                        ) : (
                                          <div className="[&_.overflow-auto]:!min-h-0">
                                            <DataTable<MergedCDType>
                                              columns={childTypeColumns}
                                              data={childColumnsByTypeId[row.id] ?? []}
                                              loading={false}
                                              emptyMessage="No child columns found."
                                              showActions={false}
                                              stickyHeader
                                            />
                                          </div>
                                        )}
                                      </div>
                                    </TableCell>
                                  </TableRow>
                                )}
                              </React.Fragment>
                            ))
                          ) : (
                            <TableRow>
                              <TableCell colSpan={8} className="h-24 text-center text-muted-foreground">No merged types found.</TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </div>
                    <div className="w-full">
                      <div className="flex items-center justify-between px-2 py-4 bg-background border-t w-full">
                        <div className="flex items-center space-x-4">
                          <div className="flex items-center space-x-2">
                            <span className="text-sm text-muted-foreground">Items per page</span>
                            <div className="relative">
                              <select
                                value={mergedTypesPageSize}
                                onChange={(e) => { setMergedTypesPageSize(Number(e.target.value)); setMergedTypesPageIndex(0); }}
                                className="h-8 w-16 rounded border border-input bg-background px-2 pr-6 text-sm focus:outline-none focus:ring-2 focus:ring-ring appearance-none [&::-webkit-appearance]:none"
                              >
                                {[50, 100, 500, 1000].map((size) => (
                                  <option key={size} value={size}>{size}</option>
                                ))}
                              </select>
                              <ChevronDown className="absolute right-1 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                            </div>
                          </div>

                          <div className="flex items-center space-x-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setMergedTypesPageIndex(mergedTypesPageIndex - 1)}
                              disabled={mergedTypesPageIndex === 0}
                              className="h-8 w-8 p-0 hover:bg-muted disabled:opacity-50 bg-transparent"
                              aria-label="Previous page"
                            >
                              <ChevronLeft className="h-4 w-4" />
                            </Button>

                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setMergedTypesPageIndex(mergedTypesPageIndex + 1)}
                              disabled={(mergedTypesPageIndex + 1) * mergedTypesPageSize >= filteredMergedTypes.length}
                              className="h-8 w-8 p-0 hover:bg-muted disabled:opacity-50 bg-transparent"
                              aria-label="Next page"
                            >
                              <ChevronRight className="h-4 w-4" />
                            </Button>
                          </div>

                          <span className="text-sm text-muted-foreground">
                            {filteredMergedTypes.length > 0 ? mergedTypesPageIndex * mergedTypesPageSize + 1 : 0} - {Math.min((mergedTypesPageIndex + 1) * mergedTypesPageSize, filteredMergedTypes.length)} of {filteredMergedTypes.length.toLocaleString()}
                          </span>
                        </div>
                        <div></div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <DataTable<CDType>
                    className="flex-1 min-h-0 overflow-auto"
                    maxHeight="calc(100vh - 280px)"
                    columns={nonMergedTypesDataTableColumns}
                    data={paginatedNonMergedTypes}
                    loading={false}
                    emptyMessage="No non-merged types found."
                    stickyHeader
                    stickyFooter
                    showActions={false}
                    onSort={(key, direction) => setSorting([{ id: key, desc: direction === "desc" }])}
                    sortConfig={sortKey ? { key: sortKey, direction: sortDesc ? "desc" : "asc" } : undefined}
                    pagination={{
                      currentPage: typesPageIndex + 1,
                      totalPages: Math.max(1, Math.ceil(sortedFilteredNonMergedTypes.length / typesPageSize)),
                      pageSize: typesPageSize,
                      totalItems: sortedFilteredNonMergedTypes.length,
                      onPageChange: (p) => setTypesPageIndex(p - 1),
                      onPageSizeChange: (size) => {
                        setTypesPageSize(size);
                        setTypesPageIndex(0);
                      },
                      pageSizeOptions: [50, 100, 500, 1000],
                    }}
                  />
                )}
              </div>
            </div>
          </TabsContent>

          {/* Values Content */}
          <TabsContent value="values" className="mt-0 flex-1 flex flex-col min-h-0">
            <div className="flex-1 flex flex-col bg-white min-h-0 rounded-lg shadow-sm overflow-hidden">
              <div className="flex-1 overflow-auto min-h-0">
                {isLoading ? (
                  <div className="flex items-center justify-center py-20">
                    <SpinnerLoader />
                  </div>
                ) : subTab === "merged" ? (
                  <div className="w-full flex flex-col flex-1 min-h-0">
                    <div className="overflow-auto relative flex-1" style={{ maxHeight: "calc(100vh - 350px)", minHeight: "calc(100vh - 350px)" }}>
                      <Table>
                        <TableHeader className="sticky top-[-1px] bg-background z-10">
                          <TableRow>
                            <TableHead className="w-12"></TableHead>
                            <TableHead className="font-semibold">Name</TableHead>
                            <TableHead className="font-semibold">Type</TableHead>
                            <TableHead className="font-semibold">Brand Name</TableHead>
                            <TableHead className="font-semibold">Brand ID</TableHead>
                            <TableHead className="font-semibold">Active</TableHead>
                            <TableHead className="font-semibold">Created By</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {paginatedMergedValues.length > 0 ? (
                            paginatedMergedValues.map((row) => (
                              <React.Fragment key={row.parent.id}>
                                <TableRow className="hover:bg-muted/50 cursor-pointer" onClick={() => toggleMergedValueRow(row.parent.id)}>
                                  <TableCell className="w-12">
                                    {row.children.length > 0 && (
                                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={(e) => { e.stopPropagation(); toggleMergedValueRow(row.parent.id); }}>
                                        {expandedMergedValues[row.parent.id] ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                      </Button>
                                    )}
                                  </TableCell>
                                  <TableCell><span className="font-medium">{row.parent.display_name || row.parent.value}</span></TableCell>
                                  <TableCell>{row.parent.custom_domain_type_id}</TableCell>
                                  <TableCell>{row.parent.brand_name ?? "N/A"}</TableCell>
                                  <TableCell>{row.parent.brand_id ?? "N/A"}</TableCell>
                                  <TableCell><Badge variant={row.parent.is_active ? "default" : "secondary"}>{row.parent.is_active ? "Yes" : "No"}</Badge></TableCell>
                                  <TableCell>
                                    <div className="flex items-center gap-2">
                                      {row.parent.user_info?.image && <img src={row.parent.user_info.image} alt="" className="w-6 h-6 rounded-full object-cover" />}
                                      <span>{row.parent.user_info?.name || "N/A"}</span>
                                    </div>
                                  </TableCell>
                                </TableRow>
                                {expandedMergedValues[row.parent.id] && row.children.length > 0 && (
                                  <TableRow>
                                    <TableCell colSpan={7} className="p-0">
                                      <div className="m-4">
                                        <p className="text-sm font-medium mb-2">Child Values for {row.parent.display_name || row.parent.value}</p>
                                        <div className="[&_.overflow-auto]:!min-h-0">
                                          <DataTable<CDValue>
                                            columns={childValueColumns}
                                            data={row.children}
                                            loading={false}
                                            emptyMessage="No child values found."
                                            showActions={false}
                                            stickyHeader
                                          />
                                        </div>
                                      </div>
                                    </TableCell>
                                  </TableRow>
                                )}
                              </React.Fragment>
                            ))
                          ) : (
                            <TableRow>
                              <TableCell colSpan={7} className="h-24 text-center text-muted-foreground">No merged values found.</TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </div>
                    <div className="w-full">
                      <div className="flex items-center justify-between px-2 py-4 bg-background border-t w-full">
                        <div className="flex items-center space-x-4">
                          <div className="flex items-center space-x-2">
                            <span className="text-sm text-muted-foreground">Items per page</span>
                            <div className="relative">
                              <select
                                value={mergedValuesPageSize}
                                onChange={(e) => { setMergedValuesPageSize(Number(e.target.value)); setMergedValuesPageIndex(0); }}
                                className="h-8 w-16 rounded border border-input bg-background px-2 pr-6 text-sm focus:outline-none focus:ring-2 focus:ring-ring appearance-none [&::-webkit-appearance]:none"
                              >
                                {[50, 100, 500, 1000].map((size) => (
                                  <option key={size} value={size}>{size}</option>
                                ))}
                              </select>
                              <ChevronDown className="absolute right-1 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                            </div>
                          </div>

                          <div className="flex items-center space-x-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setMergedValuesPageIndex(mergedValuesPageIndex - 1)}
                              disabled={mergedValuesPageIndex === 0}
                              className="h-8 w-8 p-0 hover:bg-muted disabled:opacity-50 bg-transparent"
                              aria-label="Previous page"
                            >
                              <ChevronLeft className="h-4 w-4" />
                            </Button>

                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setMergedValuesPageIndex(mergedValuesPageIndex + 1)}
                              disabled={(mergedValuesPageIndex + 1) * mergedValuesPageSize >= filteredMergedValues.length}
                              className="h-8 w-8 p-0 hover:bg-muted disabled:opacity-50 bg-transparent"
                              aria-label="Next page"
                            >
                              <ChevronRight className="h-4 w-4" />
                            </Button>
                          </div>

                          <span className="text-sm text-muted-foreground">
                            {filteredMergedValues.length > 0 ? mergedValuesPageIndex * mergedValuesPageSize + 1 : 0} - {Math.min((mergedValuesPageIndex + 1) * mergedValuesPageSize, filteredMergedValues.length)} of {filteredMergedValues.length.toLocaleString()}
                          </span>
                        </div>
                        <div></div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <DataTable<CDValue>
                    className="flex-1 min-h-0 overflow-auto"
                    maxHeight="calc(100vh - 280px)"
                    columns={nonMergedValuesDataTableColumns}
                    data={paginatedNonMergedValues}
                    loading={false}
                    emptyMessage="No non-merged values found."
                    stickyHeader
                    stickyFooter
                    showActions={false}
                    onSort={(key, direction) => setSorting([{ id: key, desc: direction === "desc" }])}
                    sortConfig={sortKey ? { key: sortKey, direction: sortDesc ? "desc" : "asc" } : undefined}
                    pagination={{
                      currentPage: valuesPageIndex + 1,
                      totalPages: Math.max(1, Math.ceil(sortedFilteredNonMergedValues.length / valuesPageSize)),
                      pageSize: valuesPageSize,
                      totalItems: sortedFilteredNonMergedValues.length,
                      onPageChange: (p) => setValuesPageIndex(p - 1),
                      onPageSizeChange: (size) => {
                        setValuesPageSize(size);
                        setValuesPageIndex(0);
                      },
                      pageSizeOptions: [50, 100, 500, 1000],
                    }}
                  />
                )}
              </div>
                      </div>
          </TabsContent>
        </Tabs>
                    </div>

      {/* Dialog Components */}
      <TaxonomyMergeTypeExistingDialog
        openMergeDialog={openTypeMergeDialog}
        setOpenMergeDialog={setOpenTypeMergeDialog}
        selectedTypes={selectedTypeIds}
        setSelectedTypes={(ids) => {
          const newSelection: RowSelectionState = {};
          ids.forEach((id) => { newSelection[id.toString()] = true; });
          setRowSelection(newSelection);
        }}
        mergedTypeGroups={mergedTypes}
        setCallTaxonomyMerge={setCallTaxonomyMerge}
        callTaxonomyMerge={callTaxonomyMerge}
        taxonomyType={globalTaxonomyType}
      />

      <TaxonomyMergeCreateDialog
        openMergeDialog={openTypeCreateDialog}
        setOpenMergeDialog={setOpenTypeCreateDialog}
        nonMergedColumns={nonMergedTypes}
        selectedCols={selectedTypeIds}
        setSelectedCols={(ids) => {
          const newSelection: RowSelectionState = {};
          ids.forEach((id) => { newSelection[id.toString()] = true; });
          setRowSelection(newSelection);
        }}
        callTaxonomyMerge={callTaxonomyMerge}
        setCallTaxonomyMerge={setCallTaxonomyMerge}
      />

      <TaxonomyMergeValueExistingDialog
        openMergeDialog={openValueMergeDialog}
        setOpenMergeDialog={setOpenValueMergeDialog}
        selectedVals={selectedValueIds}
        setSelectedVals={(ids) => {
          const newSelection: RowSelectionState = {};
          ids.forEach((id) => { newSelection[id.toString()] = true; });
          setRowSelection(newSelection);
        }}
        mergedValueGroups={mergedValues}
        callTaxonomyMerge={callTaxonomyMerge}
        setCallTaxonomyMerge={setCallTaxonomyMerge}
      />

      <TaxonomyMergeValueCreateDialog
        openMergeDialog={openValueCreateDialog}
        setOpenMergeDialog={setOpenValueCreateDialog}
        selectedVals={selectedValueIds}
        setSelectedVals={(ids) => {
          const newSelection: RowSelectionState = {};
          ids.forEach((id) => { newSelection[id.toString()] = true; });
          setRowSelection(newSelection);
        }}
        callTaxonomyMerge={callTaxonomyMerge}
        setCallTaxonomyMerge={setCallTaxonomyMerge}
        nonMergedValues={nonMergedValues}
        cdTypes={mergedTypes}
      />
    </div>
  );
};

export default TaxonomyMerge;
